import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AppShell,
  PageContainer,
  PageHeader,
  SectionContainer,
  StatCard,
  StatusBadge,
  UIButton,
} from '../../features/ui-system';
import { commandSidebarItems } from '../../features/ui-foundation/commandMenu';
import { useOperationalDataset } from './hooks/useOperationalDataset';
import { fetchExecutionLogs, getExecutionActionLabel } from './utils/executionLogApi';
import { deriveSiteImpactSummary } from './utils/siteImpact';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const severityRank = { critical: 3, high: 2, medium: 1, low: 0 };
const ticketStatusOrder = ['open', 'investigating', 'mitigated', 'closed'];

const executionActionTone = (actionType) => {
  const token = String(actionType || '').trim().toLowerCase();
  if (token === 'mitigation_completed') return 'ok';
  if (token === 'mitigation_started' || token === 'investigation_started' || token === 'backup_verified') return 'warn';
  if (token === 'ticket_created' || token === 'ticket_updated') return 'info';
  return 'neutral';
};

const normalizeCustomerState = (statusRaw) => {
  const token = String(statusRaw || '').trim().toLowerCase();
  if (['offline', 'down', 'error', 'critical', 'terminated'].includes(token)) return 'offline';
  if (['degraded', 'warning', 'warn', 'suspended', 'unstable'].includes(token)) return 'degraded';
  if (['online', 'healthy', 'active'].includes(token)) return 'online';
  return 'unknown';
};

const buildExecutionCta = (entry) => {
  if (!entry) return null;
  if (entry.ticketId) return { label: 'Open in Tickets', to: `/tickets?ticketId=${encodeURIComponent(String(entry.ticketId))}` };
  if (entry.incidentId) return { label: 'Open in Recovery', to: `/recovery?incidentId=${encodeURIComponent(String(entry.incidentId))}` };
  if (entry.routerId || entry.nodeId) {
    const nodeId = String(entry.routerId || entry.nodeId || '');
    return { label: 'Open in Topology', to: `/topology?mode=live&sourceType=nas&sourceId=${encodeURIComponent(nodeId)}&routerId=${encodeURIComponent(nodeId)}&openDetail=1` };
  }
  if (entry.customerId) return { label: 'View Customer', to: `/customers?customerId=${encodeURIComponent(String(entry.customerId))}` };
  if (entry.siteId) return { label: 'Open in Infrastructure', to: `/infrastructure?tab=sites&siteId=${encodeURIComponent(String(entry.siteId))}` };
  return null;
};

const toPriorityTone = (severity) => {
  const token = String(severity || '').trim().toLowerCase();
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

const executionContextLabel = (entry) => {
  if (!entry) return 'manual';
  if (entry.incidentId) return `incident:${entry.incidentId}`;
  if (entry.ticketId) return `ticket:${entry.ticketId}`;
  if (entry.customerId) return `customer:${entry.customerId}`;
  if (entry.routerId || entry.nodeId) return `router:${entry.routerId || entry.nodeId}`;
  if (entry.siteId) return `site:${entry.siteId}`;
  if (entry.sourceType || entry.sourceId) return `${String(entry.sourceType || 'manual')}:${String(entry.sourceId || '-')}`;
  return 'manual';
};

export default function AdminOperationsOverview() {
  const navigate = useNavigate();
  const {
    loading,
    error,
    refresh,
    incidents,
    tickets,
    customers,
    odps,
    olts,
    nasRouters,
    sites,
    lastUpdated,
  } = useOperationalDataset(navigate);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionError, setExecutionError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadExecution = async () => {
      setExecutionLoading(true);
      setExecutionError('');
      try {
        const payload = await fetchExecutionLogs({
          apiBase: API,
          navigate,
          params: { limit: 10 },
        });
        if (cancelled) return;
        setExecutionLogs(payload.items || []);
      } catch (fetchError) {
        if (cancelled) return;
        setExecutionLogs([]);
        setExecutionError(String(fetchError?.message || 'Gagal memuat update operasional.'));
      } finally {
        if (!cancelled) setExecutionLoading(false);
      }
    };
    loadExecution();
    return () => {
      cancelled = true;
    };
  }, [navigate, lastUpdated]);

  const activeIncidents = useMemo(
    () => (Array.isArray(incidents) ? incidents : []).filter((item) => !['mitigated', 'closed'].includes(String(item?.status || '').toLowerCase())),
    [incidents],
  );
  const highestSeverityIncident = useMemo(() => {
    const ordered = activeIncidents
      .slice()
      .sort((a, b) => (severityRank[String(b?.severity || 'low').toLowerCase()] || 0) - (severityRank[String(a?.severity || 'low').toLowerCase()] || 0));
    return ordered[0] || null;
  }, [activeIncidents]);
  const latestIncident = useMemo(() => activeIncidents[0] || null, [activeIncidents]);

  const activeTickets = useMemo(
    () => (Array.isArray(tickets) ? tickets : []).filter((item) => String(item?.status || '').toLowerCase() !== 'closed'),
    [tickets],
  );
  const ticketCounts = useMemo(() => {
    const base = { open: 0, investigating: 0, mitigated: 0, closed: 0, highOrCritical: 0 };
    activeTickets.forEach((ticket) => {
      const status = String(ticket?.status || 'open').toLowerCase();
      if (ticketStatusOrder.includes(status)) base[status] += 1;
      const priority = String(ticket?.priority || '').toLowerCase();
      if (priority === 'high' || priority === 'critical') base.highOrCritical += 1;
    });
    return base;
  }, [activeTickets]);

  const impactSummary = useMemo(() => {
    const impacted = (Array.isArray(customers) ? customers : [])
      .map((customer) => ({
        ...customer,
        state: normalizeCustomerState(customer?.status || customer?.status_detail?.connection_status),
      }))
      .filter((customer) => customer.state === 'offline' || customer.state === 'degraded');
    const counts = impacted.reduce((acc, customer) => {
      if (customer.state === 'offline') acc.offline += 1;
      if (customer.state === 'degraded') acc.degraded += 1;
      acc.total += 1;
      return acc;
    }, { total: 0, offline: 0, degraded: 0 });
    return { impacted, counts };
  }, [customers]);

  const siteImpactSummary = useMemo(() => deriveSiteImpactSummary({
    context: { type: 'manual', id: 'operations-overview' },
    data: {
      customers,
      incidents: activeIncidents,
      tickets: activeTickets,
      odps,
      olts,
      nasRouters,
      sites,
    },
    executionLogs,
    limit: 5,
  }), [customers, activeIncidents, activeTickets, odps, olts, nasRouters, sites, executionLogs]);

  const priorityLane = useMemo(() => {
    const items = [];
    if (highestSeverityIncident) {
      const sev = String(highestSeverityIncident.severity || 'low').toLowerCase();
      items.push({
        id: `incident-${highestSeverityIncident.id}`,
        tone: toPriorityTone(sev),
        label: 'Incident',
        title: `${highestSeverityIncident.id} | ${String(highestSeverityIncident.severity || 'low').toUpperCase()}`,
        summary: highestSeverityIncident.title || 'Incident aktif perlu perhatian.',
        cta: { label: 'Open in Recovery', to: `/recovery?incidentId=${encodeURIComponent(String(highestSeverityIncident.id || ''))}` },
        rank: severityRank[sev] || 0,
      });
    }
    const urgentTicket = activeTickets
      .slice()
      .sort((a, b) => (severityRank[String(b?.priority || 'low').toLowerCase()] || 0) - (severityRank[String(a?.priority || 'low').toLowerCase()] || 0))[0];
    if (urgentTicket) {
      const priority = String(urgentTicket.priority || 'medium').toLowerCase();
      items.push({
        id: `ticket-${urgentTicket.id}`,
        tone: toPriorityTone(priority),
        label: 'Ticket',
        title: `${urgentTicket.ticketCode || urgentTicket.id} | ${priority.toUpperCase()}`,
        summary: urgentTicket.title || 'Ticket prioritas tinggi perlu ditindaklanjuti.',
        cta: { label: 'Open in Tickets', to: `/tickets?ticketId=${encodeURIComponent(String(urgentTicket.id || ''))}` },
        rank: severityRank[priority] || 0,
      });
    }
    const topSite = siteImpactSummary.topSites?.[0];
    if (topSite) {
      const sev = String(topSite.severity || 'low').toLowerCase();
      items.push({
        id: `site-${topSite.site_id}`,
        tone: toPriorityTone(sev),
        label: 'Site',
        title: `${topSite.site_name || topSite.site_id} | ${String(topSite.severity || 'low').toUpperCase()}`,
        summary: `Impacted ${topSite.impacted_customers_total} | Offline ${topSite.offline_count} | Ticket ${topSite.active_ticket_count}`,
        cta: { label: 'Open in Topology', to: `/topology?mode=live&siteId=${encodeURIComponent(String(topSite.site_id || ''))}&openDetail=1` },
        rank: severityRank[sev] || 0,
      });
    }
    if (executionLogs[0]) {
      items.push({
        id: `log-${executionLogs[0].id || 'latest'}`,
        tone: executionActionTone(executionLogs[0].actionType),
        label: 'Operational Update',
        title: getExecutionActionLabel(executionLogs[0].actionType),
        summary: executionLogs[0].message || 'Update operasional terbaru tersedia.',
        cta: buildExecutionCta(executionLogs[0]),
        rank: 1,
      });
    }
    return items.sort((a, b) => b.rank - a.rank).slice(0, 5);
  }, [highestSeverityIncident, activeTickets, siteImpactSummary.topSites, executionLogs]);

  return (
    <AppShell sidebarItems={commandSidebarItems} activePathname="/operations" activeSearch="" initialTab="overview">
      <PageContainer>
        <PageHeader
          eyebrow="Operations"
          title="Global Operational Overview"
          subtitle="Ringkasan cepat kondisi operasional: incident, ticket, customer impact, site terdampak, dan update operasional terbaru."
          action={(
            <UIButton variant="ghost" onClick={refresh} disabled={loading || executionLoading}>
              <RefreshCw className={`h-4 w-4 ${(loading || executionLoading) ? 'animate-spin' : ''}`} />
              Refresh
            </UIButton>
          )}
        />

        {error ? (
          <SectionContainer className="mb-4 border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200">
            {error}
          </SectionContainer>
        ) : null}

        <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <StatCard title="Incident Aktif" value={activeIncidents.length} note={highestSeverityIncident ? `Highest: ${highestSeverityIncident.severity}` : 'Belum ada incident aktif'} tone={activeIncidents.length ? 'warn' : 'ok'} />
          <StatCard title="Ticket Aktif" value={activeTickets.length} note={`Open ${ticketCounts.open} | Investigating ${ticketCounts.investigating}`} tone={activeTickets.length ? 'info' : 'ok'} />
          <StatCard title="Impact Customer" value={impactSummary.counts.total} note={`Offline ${impactSummary.counts.offline} | Degraded ${impactSummary.counts.degraded}`} tone={impactSummary.counts.offline > 0 ? 'danger' : impactSummary.counts.total > 0 ? 'warn' : 'ok'} />
          <StatCard title="High/Critical Ticket" value={ticketCounts.highOrCritical} note="Prioritas tindakan cepat" tone={ticketCounts.highOrCritical > 0 ? 'danger' : 'neutral'} />
        </div>

        <SectionContainer className="mb-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Prioritas Saat Ini</p>
            <span className="text-[11px] text-slate-500">Top {priorityLane.length || 0}</span>
          </div>
          {priorityLane.length === 0 ? (
            <p className="text-sm text-slate-400">Belum ada prioritas operasional aktif.</p>
          ) : (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {priorityLane.map((item) => (
                <div key={item.id} className="rounded-md border border-white/10 bg-[#0a1322] px-2.5 py-2">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <StatusBadge tone={item.tone}>{item.label}</StatusBadge>
                    {item.cta ? <UIButton as="link" to={item.cta.to} variant="ghost">{item.cta.label}</UIButton> : null}
                  </div>
                  <p className="text-[12px] font-semibold text-slate-100">{item.title}</p>
                  <p className="text-[11px] text-slate-300">{item.summary}</p>
                </div>
              ))}
            </div>
          )}
        </SectionContainer>

        <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
          <SectionContainer className="space-y-3">
            <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Incident Summary</p>
                <UIButton as="link" to="/recovery" variant="ghost">Open in Recovery</UIButton>
              </div>
              {activeIncidents.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada incident aktif.</p>
              ) : (
                <div className="space-y-1.5 text-sm text-slate-200">
                  <p>Highest severity: <span className="text-white">{highestSeverityIncident ? `${highestSeverityIncident.id} (${String(highestSeverityIncident.severity || 'low').toUpperCase()})` : '-'}</span></p>
                  <p>Latest incident: <span className="text-white">{latestIncident ? `${latestIncident.id} - ${latestIncident.title}` : '-'}</span></p>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Ticket Summary</p>
                <UIButton as="link" to="/tickets" variant="ghost">Open in Tickets</UIButton>
              </div>
              {activeTickets.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada ticket aktif.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  <StatusBadge tone="neutral">open {ticketCounts.open}</StatusBadge>
                  <StatusBadge tone="warn">investigating {ticketCounts.investigating}</StatusBadge>
                  <StatusBadge tone="info">mitigated {ticketCounts.mitigated}</StatusBadge>
                  <StatusBadge tone={ticketCounts.highOrCritical > 0 ? 'danger' : 'neutral'}>critical/high {ticketCounts.highOrCritical}</StatusBadge>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Customer Impact Summary</p>
                <div className="flex gap-2">
                  <UIButton as="link" to="/recovery" variant="ghost">Open in Recovery</UIButton>
                  <UIButton as="link" to="/customers" variant="ghost">View Customer</UIButton>
                </div>
              </div>
              {impactSummary.counts.total === 0 ? (
                <p className="text-sm text-slate-400">Belum ada customer impact aktif.</p>
              ) : (
                <div className="space-y-1.5 text-sm text-slate-200">
                  <div className="flex flex-wrap gap-1.5">
                    <StatusBadge tone="neutral">Impacted {impactSummary.counts.total}</StatusBadge>
                    <StatusBadge tone={impactSummary.counts.offline > 0 ? 'danger' : 'neutral'}>Offline {impactSummary.counts.offline}</StatusBadge>
                    <StatusBadge tone={impactSummary.counts.degraded > 0 ? 'warn' : 'neutral'}>Degraded {impactSummary.counts.degraded}</StatusBadge>
                  </div>
                  <p>Top impacted site: <span className="text-white">{siteImpactSummary.topSites?.[0]?.site_name || '-'}</span></p>
                </div>
              )}
            </div>
          </SectionContainer>

          <SectionContainer className="space-y-3">
            <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Top Affected Sites</p>
                <div className="flex gap-2">
                  <UIButton as="link" to="/topology?mode=live" variant="ghost">Open in Topology</UIButton>
                  <UIButton as="link" to="/infrastructure?tab=sites" variant="ghost">Open in Infrastructure</UIButton>
                </div>
              </div>
              {siteImpactSummary.state !== 'ok' || (siteImpactSummary.topSites || []).length === 0 ? (
                <p className="text-sm text-slate-400">{siteImpactSummary.message || 'Belum ada dampak site yang terdeteksi.'}</p>
              ) : (
                <div className="space-y-1.5">
                  {siteImpactSummary.topSites.map((site) => (
                    <div key={site.site_id} className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2 text-xs text-slate-200">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <p className="font-semibold text-white">{site.site_name || site.site_id}</p>
                        <StatusBadge tone={site.severityTone}>{String(site.severity || 'low').toUpperCase()}</StatusBadge>
                      </div>
                      <p>Impacted {site.impacted_customers_total} | Offline {site.offline_count} | Degraded {site.degraded_count} | Incident {site.active_incident_count} | Ticket {site.active_ticket_count}</p>
                      {site.mitigation_hint ? (
                        <p className="mt-1 text-[11px] text-cyan-200">{site.mitigation_hint}</p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <UIButton as="link" to={`/recovery?siteId=${encodeURIComponent(String(site.site_id || ''))}`} variant="ghost">Open in Recovery</UIButton>
                        <UIButton as="link" to={`/topology?mode=live&siteId=${encodeURIComponent(String(site.site_id || ''))}&openDetail=1`} variant="ghost">Open in Topology</UIButton>
                        <UIButton as="link" to={`/infrastructure?tab=sites&siteId=${encodeURIComponent(String(site.site_id || ''))}`} variant="ghost">Open in Infrastructure</UIButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
              <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Last Operational Update</p>
              {executionLogs[0] ? (
                <div className="space-y-1 text-xs text-slate-300">
                  <p className="font-semibold text-white">{getExecutionActionLabel(executionLogs[0].actionType)}</p>
                  <p>{executionLogs[0].message || '-'}</p>
                  <p className="text-[11px] text-slate-500">
                    {executionLogs[0].actor || '-'} | {executionLogs[0].createdAt ? new Date(executionLogs[0].createdAt).toLocaleString('id-ID') : '-'}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-slate-400">Belum ada tindakan tercatat.</p>
              )}
            </div>

            <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Latest Operational Updates</p>
                <span className="text-[11px] text-slate-500">{lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</span>
              </div>
              {executionLoading ? <p className="text-sm text-slate-400">Masih memuat data...</p> : null}
              {!executionLoading && executionError ? <p className="text-sm text-rose-300">{executionError}</p> : null}
              {!executionLoading && !executionError && executionLogs.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada data terkait.</p>
              ) : null}
              {!executionLoading && !executionError && executionLogs.length > 0 ? (
                <div className="space-y-1.5">
                  {executionLogs.slice(0, 10).map((entry) => {
                    const cta = buildExecutionCta(entry);
                    return (
                      <div key={entry.id || `${entry.createdAt}-${entry.message}`} className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <p className="text-[12px] font-semibold text-slate-100">{getExecutionActionLabel(entry.actionType)}</p>
                            <StatusBadge tone="neutral">{executionContextLabel(entry)}</StatusBadge>
                          </div>
                          <StatusBadge tone={executionActionTone(entry.actionType)}>{entry.actor || '-'}</StatusBadge>
                        </div>
                        <p className="text-[11px] text-slate-300">{entry.message || '-'}</p>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <p className="text-[10px] text-slate-500">{entry.createdAt ? new Date(entry.createdAt).toLocaleString('id-ID') : '-'}</p>
                          {cta ? (
                            <UIButton as="link" to={cta.to} variant="ghost">
                              <ArrowUpRight className="h-3.5 w-3.5" />
                              {cta.label}
                            </UIButton>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </SectionContainer>
        </div>
      </PageContainer>
    </AppShell>
  );
}
